import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import { useAllReadModelMetadata } from '@/hooks/dashboard/useReadModelMetadata';
import type { ReadModelField } from '@/hooks/dashboard/useReadModelMetadata';
import type { WidgetConfig } from '@/hooks/dashboard/useDashboardWidgets';

interface BuiltinWidgetDef {
  type: string;
  displayName: string;
  descriptionKey: string;
  descriptionDefault: string;
  icon: React.ElementType;
  layout: { x: number; y: number; w: number; h: number };
}

const BUILTIN_WIDGETS: BuiltinWidgetDef[] = [
  {
    type: 'taskInbox',
    displayName: 'My Tasks',
    descriptionKey: 'dashboard.builtinTaskInboxDescription',
    descriptionDefault: 'Your pending workflow tasks and approvals',
    icon: InboxIcon,
    layout: { x: 0, y: 100, w: 6, h: 3 },
  },
];

interface WidgetCatalogProps {
  open: boolean;
  onClose: () => void;
  onAdd: (widget: WidgetConfig) => void;
}

type Step = 'selectMeasure' | 'configure';

const WIDGET_TYPES = [
  { value: 'kpiCard', labelKey: 'dashboard.kpiCard' },
  { value: 'lineChart', labelKey: 'dashboard.lineChart' },
  { value: 'barChart', labelKey: 'dashboard.barChart' },
] as const;

export function WidgetCatalog({ open, onClose, onAdd }: WidgetCatalogProps) {
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>('selectMeasure');
  const [selectedReadModel, setSelectedReadModel] = useState<string>('');
  const [selectedMeasure, setSelectedMeasure] = useState<ReadModelField | null>(null);
  const [widgetType, setWidgetType] = useState('kpiCard');
  const [displayName, setDisplayName] = useState('');
  const [selectedDimension, setSelectedDimension] = useState('');

  const [search, setSearch] = useState('');
  const metadataQuery = useAllReadModelMetadata();

  const handleAddBuiltin = (def: BuiltinWidgetDef) => {
    const widget: WidgetConfig = {
      id: `builtin-${def.type}-${Date.now()}`,
      type: def.type,
      displayName: def.displayName,
      layout: { ...def.layout },
      source: 'user',
    };
    onAdd(widget);
    handleClose();
  };

  const measures = useMemo(() => {
    if (!metadataQuery.data) return [];
    const all = metadataQuery.data.flatMap((rm) =>
      (rm.fields ?? [])
        .filter((f) => f.isMeasure)
        .map((f) => ({ ...f, readModel: rm.name, readModelDisplay: rm.displayName })),
    );
    if (!search.trim()) return all;
    const q = search.toLowerCase();
    return all.filter(
      (m) =>
        (m.description || m.name).toLowerCase().includes(q) ||
        m.readModelDisplay.toLowerCase().includes(q),
    );
  }, [metadataQuery.data, search]);

  // Available dimensions for the selected read model (for bar/line chart groupBy)
  const dimensions = useMemo(() => {
    if (!metadataQuery.data || !selectedReadModel) return [];
    const rm = metadataQuery.data.find((r) => r.name === selectedReadModel);
    if (!rm) return [];
    const dims = rm.fields.filter((f) => f.isDimension);
    // For daily read models, offer a "Month" groupBy option that redirects
    // to the monthly read model (e.g. SalesDaily → SalesMonthly)
    if (rm.resolution === 'day') {
      dims.push({ name: 'month', type: 'Date', isDimension: true, isMeasure: false, description: 'Month' });
    }
    return dims.sort((a, b) => (a.description || a.name).localeCompare(b.description || b.name));
  }, [metadataQuery.data, selectedReadModel]);

  const needsDimension = widgetType === 'barChart' || widgetType === 'lineChart';

  const handleSelectMeasure = (measure: ReadModelField & { readModel: string }) => {
    setSelectedReadModel(measure.readModel);
    setSelectedMeasure(measure);
    setDisplayName(measure.description || measure.name);
    setStep('configure');
  };

  const handleAdd = () => {
    if (!selectedMeasure || !selectedReadModel) return;
    if (needsDimension && !selectedDimension) return;

    const widget: WidgetConfig = {
      id: `custom-${Date.now()}`,
      type: widgetType,
      displayName,
      readModel: selectedReadModel,
      measure: selectedMeasure.name,
      aggregation: selectedMeasure.aggregation ?? 'sum',
      layout: { x: 0, y: 100, w: 3, h: widgetType === 'kpiCard' ? 1 : 3 },
      source: 'user',
      ...(widgetType === 'barChart' && selectedDimension ? { groupBy: selectedDimension } : {}),
      ...(widgetType === 'lineChart' && selectedDimension ? { dimension: selectedDimension } : {}),
    };

    onAdd(widget);
    handleClose();
  };

  const handleClose = () => {
    setStep('selectMeasure');
    setSelectedReadModel('');
    setSelectedMeasure(null);
    setWidgetType('kpiCard');
    setDisplayName('');
    setSelectedDimension('');
    setSearch('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {step === 'selectMeasure' ? t('dashboard.selectMeasure') : t('dashboard.configureWidget')}
      </DialogTitle>
      <DialogContent>
        {step === 'selectMeasure' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {/* Built-in widgets */}
            <Typography variant="overline" sx={{ px: 2, pt: 1, color: 'text.secondary' }}>
              {t('dashboard.builtinWidgets', 'Built-in Widgets')}
            </Typography>
            <List sx={{ pt: 0, pb: 0 }}>
              {BUILTIN_WIDGETS.map((def) => {
                const Icon = def.icon;
                return (
                  <ListItemButton key={def.type} onClick={() => handleAddBuiltin(def)}>
                    <Icon sx={{ mr: 2, color: 'primary.main' }} />
                    <ListItemText
                      primary={def.displayName}
                      secondary={t(def.descriptionKey, def.descriptionDefault)}
                    />
                  </ListItemButton>
                );
              })}
            </List>

            <Divider sx={{ my: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {t('dashboard.orDataWidgets', 'or choose a data widget')}
              </Typography>
            </Divider>

            {/* Search */}
            <Box sx={{ px: 2, pb: 1 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={t('dashboard.searchMeasures', 'Search measures...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                autoFocus
              />
            </Box>

            {/* Data widget measures */}
            <List sx={{ pt: 0, maxHeight: 300, overflow: 'auto' }}>
              {metadataQuery.isLoading && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t('dashboard.loadingMeasures')}
                </Typography>
              )}
              {measures.map((m) => (
                <ListItemButton
                  key={`${m.readModel}-${m.name}`}
                  onClick={() => handleSelectMeasure(m as ReadModelField & { readModel: string })}
                >
                  <ListItemText
                    primary={m.description || m.name}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={m.readModelDisplay} size="small" variant="outlined" />
                        <Chip label={m.aggregation ?? 'sum'} size="small" />
                        {m.unit && <Chip label={m.unit.label} size="small" variant="outlined" />}
                      </Box>
                    }
                  />
                </ListItemButton>
              ))}
              {!metadataQuery.isLoading && measures.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                  {t('dashboard.noMeasuresFound', 'No measures found')}
                </Typography>
              )}
            </List>
          </Box>
        )}

        {step === 'configure' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('dashboard.source', { readModel: selectedReadModel, measure: selectedMeasure?.name })}
            </Typography>
            <Divider />
            <TextField
              label={t('dashboard.displayName')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              fullWidth
              autoFocus
            />
            <FormControl fullWidth>
              <InputLabel>{t('dashboard.widgetType')}</InputLabel>
              <Select
                value={widgetType}
                label={t('dashboard.widgetType')}
                onChange={(e) => {
                  setWidgetType(e.target.value);
                  setSelectedDimension('');
                }}
              >
                {WIDGET_TYPES.map((wt) => (
                  <MenuItem key={wt.value} value={wt.value}>
                    {t(wt.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {needsDimension && (
              <FormControl fullWidth>
                <InputLabel>{t('dashboard.groupBy')}</InputLabel>
                <Select
                  value={selectedDimension}
                  label={t('dashboard.groupBy')}
                  onChange={(e) => setSelectedDimension(e.target.value)}
                >
                  {dimensions.map((d) => (
                    <MenuItem key={d.name} value={d.name}>
                      {d.description || d.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 'configure' && (
          <Button onClick={() => setStep('selectMeasure')}>{t('common.back')}</Button>
        )}
        <Button onClick={handleClose}>{t('common.cancel')}</Button>
        {step === 'configure' && (
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!displayName.trim() || (needsDimension && !selectedDimension)}
          >
            {t('dashboard.addWidget')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
